import React, {memo, useCallback} from "react";
import {useOutletContext, useParams} from "react-router-dom";
import {useDispatch} from "react-redux";
import EntitiesList from "../../../../components/Entities/EntitiesList/EntitiesList";
import StopEntityRowForm from "../Stops/StopEntityRowForm";
import {
    PublicTransitLayerEntityName,
    PublicTransitRouteProfileStopEntity
} from "../../../../api/entities/replancity_PublicTransitLine";
import AddStopToRoute from "./AddStopToRoute";
import {useEntitiesLoader} from "../../../../hooks/entites/useEntitiesLoader";
import './transit-route-stops-list.scss';
import {useEntityLoader} from "../../../../hooks/entites/useEntityLoader";
import {EntityServiceName} from "../../../../api/enums/enums";
import {FeatureProperties, FeatureState} from "../../../../redux/map/types";
import {useTypedSelector} from "../../../../redux/Hooks/storeSelectors";
import {selectMapLayerIdByEntityName} from "../../../../redux/selectors/selectors";
import {getFeatureCenter} from "../../../../utils/mapUtils";
import {TransitRouteContext} from "../../types";
import EntitiesControls from "../../../../components/Entities/EntitiesControls/EntitiesControls";
import {
    flewToPoint,
    requireToHighlightFeatures,
    setZoom
} from "../../../../redux/map/map-reducer";
import {publicTransitApi} from "../../../../api/publicTransitApi";
import {isErrorResponse} from "../../../../utils/utils";
import {useToastContext} from "../../../../context/toastContext";
import {useTranslation} from "react-i18next";


const TransitRouteStopsList = memo(() => {
    const routeProfileStopsEntityName = PublicTransitLayerEntityName.ROUTE_STOPS;
    const stopsEntityName = PublicTransitLayerEntityName.STOPS;
    const routeEntityName = PublicTransitLayerEntityName.ROUTES;
    const serviceName = EntityServiceName.PUBLIC_TRANSIT;
    const stopsLayerId: string = useTypedSelector((state) => selectMapLayerIdByEntityName(state, stopsEntityName));
    const routeLayerId: string = useTypedSelector((state) => selectMapLayerIdByEntityName(state, routeEntityName));
    const {deleteFn} = useEntityLoader<PublicTransitRouteProfileStopEntity>({entityName: routeProfileStopsEntityName});
    const {routeId} = useParams();

    const {routeProfileId, loadRoute} = useOutletContext<TransitRouteContext>();

    const updateStopsOrder = useCallback(async (entities: PublicTransitRouteProfileStopEntity[]) => {
        const abortController = new AbortController();
        const resp = await publicTransitApi.rearrangeProfileStops({
                transitRouteId: routeId!,
                profileStopsOrder: entities.map(({id}) => id)
            },
            abortController.signal
        );

        if (isErrorResponse(resp)) {
            addToast(t("common.save-error"), "error");
        } else {
            await loadRoute(true);
            addToast(t("common.save-successful"), "success");
        }
    }, [routeId, routeLayerId, loadRoute])

    const {
        loading: stopsLoading,
        entities: routeStops,
        loadData: loadStopsData,
        loadFeatureOnRowClick,
        searchPhrase,
        setSearch,
        updateEntitiesOrder
    } = useEntitiesLoader<PublicTransitRouteProfileStopEntity>({
        entityName: routeProfileStopsEntityName,
        serviceName: serviceName,
        parentId: routeId,
        updateListOrderFn: updateStopsOrder
    });
    const dispatch = useDispatch();
    const {addToast} = useToastContext();
    const {t} = useTranslation();

    //TODO here were clearedSelectedMapFeatures and enableLayersInteractivity called

    const onStopAddOrDelFn = useCallback(async (reloadLayer = false) => {
        await loadStopsData();
        await loadRoute(reloadLayer);
    }, [loadStopsData, loadRoute])

    const deleteRouteStop = useCallback(async ({entity}: { entity: PublicTransitRouteProfileStopEntity }) => {
        await deleteFn({id: entity.id});
        await onStopAddOrDelFn(true);
    }, [loadStopsData, loadRoute, deleteFn, onStopAddOrDelFn])

    const rowClickHandler = useCallback(async (entity: PublicTransitRouteProfileStopEntity) => {
        // await onRowClick(entity);

        try {
            const stopFeature = JSON.parse(entity?.transitStop?.geometryJson);

            const [lng, lat] = getFeatureCenter(stopFeature);
            if (lng && lat) {
                dispatch(flewToPoint({lng, lat, zoom: 15}));
            }

            //TODO stops already highlighted. How to highlight clicked stop in addition?
            const {properties: {id, featureStateId}} = stopFeature as GeoJSON.Feature<GeoJSON.Point, FeatureProperties>;

            dispatch(requireToHighlightFeatures({
                featureProperties: {
                    id,
                    featureStateId: featureStateId.toString(),
                    layerId: stopsLayerId,
                    entityName: stopsEntityName,
                    stateProperty: FeatureState.HIGHLIGHTED
                }
            }));

            dispatch(setZoom(15));
        } catch (e) {
            console.error('Replan. geometryJson is absent in entity.transitStop', entity, e);
        }
    }, [stopsLayerId, stopsEntityName])

    return (
        <>
            <div className="transit-route-stops-controls">
                <EntitiesControls
                    searchPhrase={searchPhrase}
                    onSearch={setSearch}
                    showNewButton={false}
                    controls={
                        <AddStopToRoute
                            routeProfileId={routeProfileId}
                            onStopAdditionFn={onStopAddOrDelFn}
                        />
                    }
                />
            </div>
            <EntitiesList<PublicTransitRouteProfileStopEntity>
                loading={stopsLoading}
                entities={routeStops}
                showNumbering={true}
                form={StopEntityRowForm}
                onRowClick={rowClickHandler}
                // getUrlFunc={getUrlFunc}
                deleteFn={deleteRouteStop}
                loadDataFn={loadStopsData}
                hasLink={false}
                draggable={true}
                onUpdateListOrderFn={updateEntitiesOrder}
            />
        </>
    )
})

export default TransitRouteStopsList;